import * as XLSX from 'xlsx';
import type { InventoryCycleReport } from '../services/stockCountService';
import { fromMinorUnitsOrNull } from './money';

/**
 * STOCKCOUNT-RECON.1 — Inventory Cycle Report workbook.
 *
 * Renders the backend report JSON into a 3-sheet .xlsx (Summary / Movement
 * Detail / Metadata). This is a HISTORICAL report for a posted count, so all
 * cells are STATIC values (no formulas).
 */

const numOrBlank = (n: number | null | undefined): number | '' => (n == null ? '' : n);

const fmtDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString();
};

const SUMMARY_HEADERS = [
  'Inventory Item ID', 'Short ID', 'Item Name', 'Item Type', 'Category', 'Subcategory',
  'Size', 'Unit', 'Location',
  'Opening Quantity', 'Received', 'Transfer In', 'Adjustment +', 'Sales / Depletion',
  'Waste', 'Transfer Out', 'Adjustment -', 'Reversal / Void',
  'Expected Quantity', 'Physical Quantity', 'Variance Quantity',
  'Unit Cost', 'Expected Value', 'Physical Value', 'Variance Value',
];

const MOVEMENT_HEADERS = [
  'Date', 'Inventory Item ID', 'Short ID', 'Item Name', 'Location', 'Movement Type', 'Bucket',
  'Quantity', 'Unit Cost', 'Total Cost', 'Reference Type', 'Reference ID',
  // POS-ORDER-NUMBER.1 — beside the id, never instead of it. Blank on every
  // row posted before the sender included one and on every reference type
  // that has no human number. Named as the register names it.
  'Order Number', 'Posted By', 'Notes',
];

/** Build the workbook and trigger a client-side download. No backend calls. */
export function downloadInventoryCycleReport(report: InventoryCycleReport): void {
  const { meta, rows, movements } = report;
  const wb = XLSX.utils.book_new();

  // ── Sheet 1: Summary ──────────────────────────────────────────────────────
  const summaryAoa: (string | number)[][] = [
    SUMMARY_HEADERS,
    ...rows.map(r => [
      r.inventoryItemId,
      r.shortId ?? '',
      r.itemName ?? '',
      r.itemType ?? '',
      r.category ?? '',
      r.subcategory ?? '',
      r.sizeValue != null ? r.sizeValue : '',
      r.sizeUnit ?? '',
      r.locationName ?? '',
      numOrBlank(r.openingQuantity),
      numOrBlank(r.received),
      numOrBlank(r.transferIn),
      numOrBlank(r.adjustmentPlus),
      numOrBlank(r.salesDepletion),
      numOrBlank(r.waste),
      numOrBlank(r.transferOut),
      numOrBlank(r.adjustmentMinus),
      numOrBlank(r.reversalVoid),
      numOrBlank(r.expectedQuantity),
      numOrBlank(r.physicalQuantity),
      numOrBlank(r.varianceQuantity),
      numOrBlank(fromMinorUnitsOrNull(r.unitCost)),
      numOrBlank(fromMinorUnitsOrNull(r.expectedValue)),
      numOrBlank(fromMinorUnitsOrNull(r.physicalValue)),
      numOrBlank(fromMinorUnitsOrNull(r.varianceValue)),
    ]),
  ];
  const summaryWs = XLSX.utils.aoa_to_sheet(summaryAoa);
  summaryWs['!cols'] = SUMMARY_HEADERS.map(h => ({ wch: Math.max(h.length + 2, 12) }));
  XLSX.utils.book_append_sheet(wb, summaryWs, 'Summary');

  // ── Sheet 2: Movement Detail ──────────────────────────────────────────────
  const movementAoa: (string | number)[][] = [
    MOVEMENT_HEADERS,
    ...movements.map(m => [
      fmtDateTime(m.date),
      m.inventoryItemId,
      m.shortId ?? '',
      m.itemName ?? '',
      m.location ?? '',
      m.movementType,
      m.bucket ?? '',
      numOrBlank(m.quantity),
      numOrBlank(fromMinorUnitsOrNull(m.unitCost)),
      numOrBlank(fromMinorUnitsOrNull(m.totalCost)),
      m.sourceDocumentType ?? '',
      m.sourceDocumentId ?? '',
      m.sourceDocumentNumber ?? '',
      m.postedBy ?? '',
      m.notes ?? '',
    ]),
  ];
  const movementWs = XLSX.utils.aoa_to_sheet(movementAoa);
  movementWs['!cols'] = MOVEMENT_HEADERS.map(h => ({ wch: Math.max(h.length + 2, 12) }));
  XLSX.utils.book_append_sheet(wb, movementWs, 'Movement Detail');

  // ── Sheet 3: Metadata ─────────────────────────────────────────────────────
  const metaAoa: (string | number)[][] = [
    ['Field', 'Value'],
    ['Report Name', meta.reportName],
    ['Venue', meta.venueName ?? meta.venueId],
    ['Location', meta.locationName ?? (meta.locationId ?? '')],
    ['Current Count ID', meta.currentCount.id],
    ['Current Count Posted At', fmtDateTime(meta.currentCount.postedAt)],
    ['Previous Count ID', meta.previousCount?.id ?? ''],
    ['Previous Count Posted At', fmtDateTime(meta.previousCount?.postedAt)],
    ['Period Start', fmtDateTime(meta.periodStart)],
    ['Period End', fmtDateTime(meta.periodEnd)],
    ['Opening Basis', meta.openingBasis],
    ['Cost Basis', meta.costBasis],
    ['Generated At', fmtDateTime(meta.generatedAt)],
    ...meta.notes.map((n, i) => [i === 0 ? 'Notes' : '', n]),
  ];
  const metaWs = XLSX.utils.aoa_to_sheet(metaAoa);
  metaWs['!cols'] = [{ wch: 26 }, { wch: 80 }];
  XLSX.utils.book_append_sheet(wb, metaWs, 'Metadata');

  XLSX.writeFile(wb, `inventory-cycle-report-${meta.currentCount.id}.xlsx`);
}
